import {Entity} from "./entity";
import {Issue} from "./issue.entity";
import {PersonalLabel} from "./personal-label.entity";
import {Expose, Transform} from "class-transformer";
import {container} from "tsyringe";
import {IssueRepository} from "../repositories/issue.repository";
import {PersonalLabelRepository} from "../repositories/personal-label.repository";
import {ChangeTracker} from "../../change-tracking/change-tracker";
import {DateTime} from "luxon";
import {TimeCluster} from "../../types/time-cluster.type";
import {Public} from "../../decorators/public.decorator";

/**
 * One row per (User, Issue): the User's own private label assignment + time
 * cluster + date for that Issue — the frontend counterpart of the backend
 * `IssuePersonalData` entity (see its own doc comment for why these three
 * fields share one table/entity instead of three).
 *
 * No `owner`/`deletedAt` field: the backend never accepts an `owner` from the
 * payload (Create sets it from the session, Update never reads it at all —
 * see the backend handlers), and the table has no soft-delete column at all
 * (hard-deleted, unlike `personal_labels` — see the backend entity's own doc
 * comment on why).
 *
 * `@Public()` on `personalLabel`/`sortOrder` (added 2026-09-06, "My Organizer"
 * reorder step) — WITHOUT it, `ChangeTracker`'s Proxy (see its own doc comment)
 * never tracks a mutation on an already-persisted instance, so
 * `getChangedValues()` sends only `{uid}` for these two fields; the four
 * column-assignment commands (Assign/Unassign/Move/Reorder) would silently
 * no-op for any row that wasn't brand new. This was previously the case (a live,
 * shipped bug — re-assigning a label after an unassign never actually
 * persisted) until this fix. Deliberately NOT added to `issue` (never mutated
 * after creation).
 *
 * `personalDate` ALSO carries `@Public()` now (added 2026-09-07, "Mein
 * Kalender"). It used to be deliberately excluded here, for a real reason:
 * `getChangedValues()` (`ChangeTracker`) reads the RAW tracked value, bypassing
 * this field's own `toPlainOnly` `@Transform` (`DateTime` → ISO date string) —
 * an update command's payload would have carried a raw Luxon `DateTime`
 * instead of `"YYYY-MM-DD"`, which the backend's `IssuePersonalDataValidation
 * ::parsePersonalDate()` rejects outright. That asymmetry is fixed at its
 * actual source now — `TransformerHelper.toDTO()`'s update-path runs
 * `instanceToPlain()` (the same, already-`@Transform`-aware call Create uses)
 * and intersects it with `changeTracker.getChangedPropertyKeys()`, rather than
 * reading `getChangedValues()`'s raw values — so `personalDate` (and any other
 * `@Public()` field with a transform) serializes correctly on update too, not
 * just on create. `timeCluster` stays un-tracked: no calendar UI writes it yet
 * (see `MyOrganizerNotificationService`'s own note anticipating this field).
 */
export class IssuePersonalData extends Entity<IssuePersonalData> {

    @Expose()
    @Transform(({value}) => {
        if (!value) {
            return null;
        }
        const promise = container.resolve(IssueRepository).findOneById(value);
        Reflect.set(promise, `uid`, value);
        return promise;
    }, {toClassOnly: true})
    @Transform(({value}: { value: Promise<Issue> & { uid: string } }) => value.uid, {toPlainOnly: true})
    issue: Promise<Issue> & { uid: string } | null = null;

    @Public()
    @Expose()
    @Transform(({value}) => {
        if (!value) {
            return null;
        }
        const promise = container.resolve(PersonalLabelRepository).findOneById(value);
        Reflect.set(promise, `uid`, value);
        return promise;
    }, {toClassOnly: true})
    @Transform(({value}: { value: (Promise<PersonalLabel> & { uid: string }) | null }) => value?.uid ?? null, {toPlainOnly: true})
    personalLabel: Promise<PersonalLabel> & { uid: string } | null = null;

    @Expose()
    timeCluster: TimeCluster | null = null;

    /**
     * Plain date, NOT the UTC-DateTime transform `deletedAt` elsewhere in this
     * codebase uses (e.g. EmployeeToIssueAssignment.deletedAt) — that pattern
     * is for a DATETIME column and would risk an off-by-one-day shift here,
     * since `personal_date` is a DATE-only column on the backend (see its own
     * entity doc comment). `fromISO`/`toISODate` never touch a time zone.
     */
    @Public()
    @Expose()
    @Transform(({value}) => !value ? null : DateTime.fromISO(value), {toClassOnly: true})
    @Transform(({value}: { value: DateTime | null }) => value?.toISODate() ?? null, {toPlainOnly: true})
    personalDate: DateTime | null = null;

    /**
     * Manual ordering within a personal-label column ("My Organizer" reorder
     * step, 2026-09-06). `0` is a reserved sentinel meaning "never positioned in
     * a column" — see the backend entity's own doc comment. Gap-based (steps of
     * 100), computed client-side and set on this proxy before dispatching
     * `ReorderIssueOfPersonalLabelCommand` (or optionally alongside Assign/Move
     * for a positionally-precise drop).
     */
    @Public()
    @Expose()
    sortOrder: number = 0;

    constructor() {
        super();

        return ChangeTracker.create(this as IssuePersonalData);
    }

    public toString(): string {
        return "Persönliche Issue-Daten";
    }
}
